import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import { Checkbox, Form } from "antd";
import { FormContainer } from "../InmateDetails/styles";
import {
  formatDropDownOptions,
  formatDropDownOptionsWithRefListId,
  getOptionsFromRefList,
  isNonEmptyVariable,
} from "@/util/common_function";
import dayjs from "dayjs";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { labels } from "@/util/label";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import CommonDatePicker from "@/common/elements/DatePicker";
import FileUpload from "@/common/elements/InputElements/Upload";
import BusinessInformation from "../AddInmateMovement/BusinessInformation";
import { INMATE_DETAILS_MODE } from "../InmateDetails";
import TextField from "@/common/elements/InputElements/TextField";
import Button from "@/common/elements/Button";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import TransportDocumentModal from "../InmateDetails/TransportDocumentModal";
import BlockSuggestionPopup from "./BlockSuggestion";
import CellSuggestionPopup from "./CellSuggestion";

const InmateMovementDetails = forwardRef(
  (
    {
      data,
      bookingsFieldsEnabled,
      mode,
      isScheduled = false,
      isReturn = false,
      newFormDisable,
      isInLoan = false,
    },
    ref
  ) => {
    const [form] = Form.useForm();

    const [inmateTrack, setInmateTrack] = useState();
    const [isTransportModal, setIsTransportModal] = useState(false);
    const [suggestionData, setSuggestionData] = useState([]);
    const [showBlockSuggestion, setShowBlockSuggestion] = useState(false);
    const [showCellSuggestion, setShowCellSuggestion] = useState(false);
    const [blockOptions, setBlockOptions] = useState();
    const [cellOptions, setCellOptions] = useState();
    const [bedOptions, setBedOptions] = useState();
    const [destinationOptions, setDestinationOptions] = useState();
    const movementStatus = Form.useWatch("movementStatus", form);
    const inmateLocation = Form.useWatch("inmateLocation", form);
    const inJailType = Form.useWatch("inJailType", form);
    const loanType = Form.useWatch("loanType", form);
    const enterBusiness = Form.useWatch("enterBusiness", form) || false;
    const movementReason = Form.useWatch("moveReason", form);
    const [movementstatus, setMovementStatus] = useState([]);
    const fullSearch = useSelector(state => state.fullSearch);
    const isEnabled =
      (bookingsFieldsEnabled &&
      !newFormDisable &&
      data?.inmateLocation !== "CLEARED" &&
      (data?.movementStatus !== "Complete" || data.comments === "RETURNING INMATE FROM LOAN TO CELL.") &&
      data?.movementStatus !== "Cancelled") || fullSearch;
    const disableFields = fullSearch ? false : (data?.movementStatus === "Pending");

    useEffect(() => {      
      setInmateTrack(() => ({
        ...data,
        expectedBackDateTime: isNonEmptyVariable(data?.expectedBackDate)
          ? dayjs(
              `${data?.expectedBackDate || ""} ${data?.expectedBackTime || ""}`
            )
          : null,
        scheduleDateTime: isNonEmptyVariable(data?.scheduledDate)
          ? dayjs(`${data?.scheduledDate || ""} ${data?.scheduledTime || ""}`)
          : null,
        actualDateTime: isNonEmptyVariable(data?.actualDate)
          ? dayjs(`${data?.actualDate || ""} ${data?.actualTime || ""}`)
          : fullSearch ? "" : dayjs(),
        businesses: data?.enterBusiness ? data?.businesses : null,
        returnBackDateTime:
      data?.returnDate && data?.returnTime
        ? dayjs(`${data.returnDate} ${data.returnTime}`)
        : isReturn
        ? dayjs(new Date())
        : null,
      }));
    }, [data]);

    useEffect(() => {
      if (isNonEmptyVariable(inmateTrack)) {
        form.setFieldsValue(inmateTrack);
      } else {
        form.resetFields();
      }
    }, [inmateTrack]);
    useEffect(() => {
      let mvnt = getOptionsFromRefList("Movement_Status");
      if (!isScheduled) {
        if (isInLoan) {
          let newMvnt = mvnt.filter((e, i) => e?.value != "Complete");
          setMovementStatus([...newMvnt]);
        } else {
          setMovementStatus([...mvnt]);
        }
      } else {
        if (isInLoan) {
          let newMvnt = mvnt.filter((e, i) => e?.value != "Complete");
          setMovementStatus([...newMvnt]);
        } else {
          setMovementStatus([...mvnt]);
        }
      }
    }, [isInLoan, isScheduled]);

    const handleTransportDocument = () => {
      setIsTransportModal(!isTransportModal);
    };

    const handlecellBedListOnChange = (value, field) => {
      if (!isNonEmptyVariable(value)) return;
      let url =
        field === "blockList" ? 
          (value === "Holding" ? 
            `${API_ENDPOINTS.cellBedList}/holdingCellList` : `${API_ENDPOINTS.cellBedList}/blockList` )
          : `${API_ENDPOINTS.cellBedList}/${field}/${value}`;

      api.get(url).then(({ data }) => {
        if (field === "blockList") {
          const formattedData = formatOptions(data, field);
          setBlockOptions(formattedData);
        } else if (field === "cellList") {
          setCellOptions(formatDropDownOptions(data));
        } else {
          setBedOptions(formatDropDownOptions(data));
        }
      });
    };

    const formatOptions = (items = [], field) => {
      let filteredItems = items.filter((rli) => rli?.enabled) || [];
      filteredItems.unshift({
        refListItemGlobalID: "",
        defaultString: "",
        longString: "",
        enabled: true,
      });
      if (field === "blockList") {
        return filteredItems
          .filter(aa => aa?.defaultString)
          .map(aa => ({
            value: aa.defaultString,
            enabled: aa?.enabled,
            label: aa?.longString || aa.defaultString,
          }))
          .sort((a, b) => a.value.localeCompare(b.value));
      }
      return formatDropDownOptions(filteredItems);
    };

    const handleDestinationListOnChange = (value) => {
      if (!isNonEmptyVariable(value)) {
        return;
      }
      const url = `${API_ENDPOINTS.destinationList}/${value}`;
      api.get(url).then(({ data }) => {
        const formattedData = formatDropDownOptionsWithRefListId(data, true);
        setDestinationOptions(formattedData);
      });
    };

    // Expose the form methods to parent
    useImperativeHandle(ref, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      setFormValue: () => form.setFieldValue(),
      getFieldsError: () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields: () => form.validateFields(),
    }));

    const handleAutoFill = () => {
      const url = API_ENDPOINTS.HousingSuggestion;
      api.get(url).then(({ data }) => {
        form.setFieldValue("block", data?.block);
        form.setFieldValue("cell", data?.cell);
        form.setFieldValue("bed", data?.bed);
      });
    };

    const handleSuggestion = (location) => {
      const payload = [];

      const block = form.getFieldValue("block");

      if (!block) {
        return;
      }

      payload.push({
        filterKey: "housingAssignmentBlock",
        value: block,
      });

      if (location === "cell") {
        const cell = form.getFieldValue("cell");

        if (!cell) {
          return;
        }
        payload.push({
          filterKey: "housingAssignmentCell",
          value: cell,
        });
      }
      if (location === "cell") {
        setShowCellSuggestion(true);
      } else {
        setShowBlockSuggestion(true);
      }
      const url = API_ENDPOINTS.cellBlockSuggestion;

      api
        .post(url, payload)
        .then(({ data }) => {
          if (data) {
            setSuggestionData(data);
          }
        })
        .catch((err) => {});
    };

    return (
      <>
        <FormContainer>
          <Form
            form={form}
            initialValues={inmateTrack}
            disabled={!isEnabled}
          >
            <StyledGridRow gutter={[16]}>
              <StyledGridCol span={6}>
                <FormItem
                  name="inmateLocation"
                  label={labels.moveTo}
                  required={!fullSearch}
                  rules={
                    !fullSearch
                    ? [
                      {
                        required: true,
                        message: "Move To is required",
                      },
                    ] : []
                  }
                >
                  <Select
                    showSearch={true}
                    variant="outlined"
                    options={getOptionsFromRefList("Inmate_Location")}
                    disabled={!isEnabled || disableFields}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={6}>
                <FormItem
                  name="movementStatus"
                  label={labels.status}
                  required={!fullSearch}
                  rules={!fullSearch ? [{ required: true, message: "Status is required" }] : []}
                >
                  <Select
                    onSelect={(value) => {
                      if (value === "Cancelled" || value === "Pending") {
                        form.setFieldValue("actualDateTime", null);
                        form.setFieldValue("scheduleDateTime", null);
                      } else if (value === "Complete") {
                        form.setFieldValue("scheduleDateTime", null);
                        if(!fullSearch) {
                          form.setFieldValue("actualDateTime", dayjs());
                        }
                      }
                    }}
                    showSearch={true}
                    variant="outlined"
                    options={movementstatus}
                    disabled={!isEnabled}
                    required
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={6}>
                {movementStatus === "Pending" && (
                  <FormItem
                    name="scheduleDateTime"
                    label={labels.schduleDateTime}
                    rules={
                      !fullSearch ? [
                      {
                        required: true,
                        message: "Schedule date & time is required",
                      },
                    ] : []}
                  >
                    <CommonDatePicker
                      disabled={!isEnabled}
                      placeholder="MM/DD/YYYY HH:mm"
                      format="MM/DD/YYYY HH:mm"
                      showTime={true}
                      style={{
                        border: "2px solid rgba(82, 140, 181, 0.27)",
                      }}
                    />
                  </FormItem>
                )}
              </StyledGridCol>

              <StyledGridCol span={6}>
                <FormItem name="actualDateTime" label={labels.actualDateTime}>
                  <CommonDatePicker
                    disabled={!isEnabled}
                    placeholder="MM/DD/YYYY HH:mm"
                    format="MM/DD/YYYY HH:mm"
                    showTime={true}
                    disableFutureDates={true}
                    style={{
                      border: "2px solid rgba(82, 140, 181, 0.27)",
                    }}
                  />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>

            {inmateLocation === "In Jail" && (
              <StyledGridRow gutter={[16]}>
                <StyledGridCol span={6}>
                  <FormItem
                    label={labels.jailType}
                    name="inJailType"
                    rules={
                      !fullSearch ? [
                      { required: true, message: "Jail Type is required" },
                    ] : []}
                  >
                    <Select
                      placeholder="Jail Type"
                      showSearch={true}
                      variant="outlined"
                      options={getOptionsFromRefList("Housing_Type")}
                      disabled={!isEnabled || disableFields}
                      onChange={(value) =>
                        handlecellBedListOnChange(value, "blockList")
                      }
                    />
                  </FormItem>
                </StyledGridCol>
                {inJailType === "Cell" && (
                  <>
                    <StyledGridCol span={4}>
                      <FormItem
                        name="block"
                        label="block"
                        rules={
                          !fullSearch ? [
                          { required: true, message: "Block is required" },
                        ] : []}
                      >
                        <Select
                          showSearch={true}
                          variant="outlined"
                          // options={getOptionsFromRefList("Block")}
                          options={
                            Array.isArray(blockOptions)
                              ? blockOptions
                              : getOptionsFromRefList("Block")
                          }
                          disabled={!isEnabled}
                          onChange={(value) =>
                            handlecellBedListOnChange(value, "cellList")
                          }
                        />
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={1}>
                      <FormItem name="block-suggestion" paddingTop={"28px"}>
                        <Button
                          type="secondary"
                          color="secondary"
                          disabled={!isEnabled}
                          onClick={() => handleSuggestion("block")}
                        >
                          {"."}
                        </Button>
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={4}>
                      <FormItem name="cell" label="Cell">
                        <Select
                          showSearch={true}
                          variant="outlined"
                          options={
                            Array.isArray(cellOptions)
                              ? cellOptions
                              : getOptionsFromRefList("Cell")
                          }
                          disabled={!isEnabled}
                          onChange={(value) =>
                            handlecellBedListOnChange(value, "bedList")
                          }
                        />
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={1}>
                      <FormItem name="cell-suggestion" paddingTop={"28px"}>
                        <Button
                          type="secondary"
                          color="secondary"
                          disabled={!isEnabled}
                          onClick={() => handleSuggestion("cell")}
                        >
                          {"."}
                        </Button>
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={4}>
                      <FormItem name="bed" label={labels.bed}>
                        <Select
                          showSearch={true}
                          variant="outlined"
                          options={
                            Array.isArray(bedOptions)
                              ? bedOptions
                              : getOptionsFromRefList("Bed")
                          }
                          disabled={!isEnabled}
                        />
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={1}>
                      <FormItem name="autofill" paddingTop={"28px"}>
                        <Button
                          type="secondary"
                          color="secondary"
                          disabled={!isEnabled}
                          onClick={handleAutoFill}
                        >
                          {" ? "}
                        </Button>
                      </FormItem>
                    </StyledGridCol>
                  </>
                )}

                {inJailType === "Holding" && (
                  <StyledGridRow gutter={16}>
                    <StyledGridCol span={24}>
                      <FormItem
                        name="holdingCell"
                        label={labels.holdingIslolationCell}
                        rules={
                          !fullSearch ? [
                          { required: true, message: "Holding / Isolation Cell is required" },
                        ] : []}
                      >
                        <Select
                          placeholder="Holding/Isolation Cell"
                          showSearch={true}
                          variant="outlined"
                          // options={getOptionsFromRefList("Cells_Holding")}
                          options={
                            Array.isArray(blockOptions)
                              ? blockOptions
                              : getOptionsFromRefList("Block")
                          }
                          disabled={!isEnabled}
                        />
                      </FormItem>
                    </StyledGridCol>
                  </StyledGridRow>
                )}
              </StyledGridRow>
            )}

            {inmateLocation === "Loan" && (
              <StyledGridRow gutter={[16]}>
                <StyledGridCol span={7}>
                  <FormItem
                    name="loanType"
                    label={labels.loanType}
                    rules={
                      !fullSearch ? [
                      { required: true, message: "Loan Type is required" },
                    ] : []}
                  >
                    <Select
                      placeholder="Loan Type"
                      showSearch={true}
                      variant="outlined"
                      options={getOptionsFromRefList("Loan_Type")}
                      onChange={(value) => handleDestinationListOnChange(value)}
                      disabled={!isEnabled || disableFields}
                    />
                  </FormItem>
                </StyledGridCol>

                {loanType !== "Court" && loanType !== "Program" && (
                  <StyledGridCol span={8}>
                    <FormItem
                      name="loanDestination"
                      label={labels.destination}
                      rules={
                        !fullSearch ? [
                        {
                          required: true,
                          message: "Loan destination is required",
                        },
                      ] : []}
                    >
                      <Select
                        showSearch={true}
                        placeholder="Destination"
                        variant="outlined"
                        options={
                          Array.isArray(destinationOptions)
                            ? destinationOptions
                            : getOptionsFromRefList("Loan_Destination")
                        }
                        disabled={!isEnabled || disableFields}
                      />
                    </FormItem>
                  </StyledGridCol>
                )}

                {loanType === "Court" && (
                  <StyledGridCol span={8}>
                    <FormItem
                      name="court"
                      label="Court"
                      rules={
                        !fullSearch ? [
                        {
                          required: true,
                          message: "Court is required",
                        },
                      ] : []}
                    >
                      <Select
                        showSearch={true}
                        placeholder="Court"
                        variant="outlined"
                        options={getOptionsFromRefList("Court")}
                        disabled={!isEnabled}
                      />
                    </FormItem>
                  </StyledGridCol>
                )}

                {loanType === "Program" && (
                  <StyledGridCol span={8}>
                    <FormItem
                      name="programName"
                      label="Program"
                      rules={
                        !fullSearch ? [
                        {
                          required: true,
                          message: "Program is required",
                        },
                      ] : []}
                    >
                      <Select
                        showSearch={true}
                        placeholder="Program"
                        variant="outlined"
                        options={getOptionsFromRefList("Program_Name")}
                        disabled={!isEnabled}
                      />
                    </FormItem>
                  </StyledGridCol>
                )}

                <StyledGridCol span={9}>
                  <FormItem
                    name="expectedBackDateTime"
                    label={labels.expectedDateTime}
                    rules={
                      !fullSearch ? [
                      {
                        required: true,
                        message: "Expected back date & time is required",
                      },
                    ] : []}
                  >
                    <CommonDatePicker
                      placeholder="MM/DD/YYYY HH:mm"
                      format="MM/DD/YYYY HH:mm"
                      showTime={true}
                      disabled={!isEnabled && !isReturn}
                      style={{
                        border: "2px solid rgba(82, 140, 181, 0.27)",
                      }}
                    />
                  </FormItem>
                  {(isReturn || inmateTrack?.returnBackDateTime) && (
                    <FormItem
                      name="returnBackDateTime"
                      label="Return Date Time"
                    >
                      <CommonDatePicker
                        placeholder="MM/DD/YYYY HH:mm"
                        format="MM/DD/YYYY HH:mm"
                        showTime={true}
                        disabled={(!isEnabled || disableFields) && !isReturn}
                        style={{
                          border: "2px solid rgba(82, 140, 181, 0.27)",
                        }}
                      />
                    </FormItem>
                  )}
                </StyledGridCol>
              </StyledGridRow>
            )}

            {inmateLocation === "Perm Housing Change" && (
              <StyledGridRow gutter={[16]}>
                <StyledGridCol span={7}>
                  <FormItem
                    name="transportDestination"
                    label={labels.destination}
                    rules={
                      !fullSearch ? [
                      {
                        required: true,
                        message: "Transport destination is required",
                      },
                    ] : []}
                  >
                    <Select
                      placeholder="Destination"
                      variant="outlined"
                      showSearch={true}
                      options={getOptionsFromRefList("HOUSING_AGENCY")}
                      disabled={!isEnabled || disableFields}
                    />
                  </FormItem>
                </StyledGridCol>

                <StyledGridCol span={5}>
                  <FormItem name="transDoc" label={""} paddingTop="30px">
                    <Button
                      type="secondary"
                      color="secondary"
                      onClick={handleTransportDocument}
                    >
                      Transport Document
                    </Button>
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
            )}

            {inmateLocation == "Transport" && (
              <StyledGridRow gutter={[16]}>
                <StyledGridCol span={7}>
                  <FormItem
                    name="transportDestination"
                    label={labels.destination}
                    rules={
                      !fullSearch ? [
                      {
                        required: true,
                        message: "Transport destination is required",
                      },
                    ] : []}
                  >
                    <Select
                      placeholder="Destination"
                      variant="outlined"
                      showSearch={true}
                      options={[
                        {
                          value: "Workhouse",
                          enabled: true,
                          label: "Workhouse",
                        },
                      ]}
                      disabled={!isEnabled || disableFields}
                    />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={5}>
                  {/* <FormItem name="transDoc" label={labels.transportDocument}>
                  <FileUpload documentType="transport document"></FileUpload>
                </FormItem> */}
                  <FormItem name="transDoc" label={""} paddingTop="30px">
                    <Button
                      type="secondary"
                      color="secondary"
                      onClick={handleTransportDocument}
                    >
                      Transport Document
                    </Button>
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <FormItem
                    name="expectedBackDateTime"
                    label={labels.expectedDateTime}
                  >
                    <CommonDatePicker
                      style={{
                        border: "2px solid rgba(82, 140, 181, 0.27)",
                      }}
                      placeholder="MM/DD/YYYY HH:mm"
                      format="MM/DD/YYYY HH:mm"
                    />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
            )}

            <StyledGridRow gutter={[16]}>
              <StyledGridCol span={5}>
                <FormItem name="moveReason" label={labels.reasonForMove}>
                  <Select
                    placeholder="Reason for Move"
                    variant="outlined"
                    showSearch={true}
                    options={getOptionsFromRefList("Move_Reason")}
                    disabled={!isEnabled || disableFields}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={6}>
                <FormItem
                  name="approvingOfficerStaffID"
                  label={labels?.approvalOfficer}
                >
                  <Select
                    placeholder={labels?.approvalOfficer}
                    readOnly={mode === INMATE_DETAILS_MODE.VIEW}
                    showSearch={true}
                    options={getOptionsFromRefList("", true, false, true)}
                    disabled={!isEnabled || disableFields}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol span={6}>
                <FormItem
                  name="transportingOfficerStaffID"
                  label={labels?.transportingOfficer}
                >
                  <Select
                    placeholder={labels?.transportingOfficer}
                    readOnly={mode === INMATE_DETAILS_MODE.VIEW}
                    showSearch={true}
                    options={getOptionsFromRefList("", true)}
                    disabled={!isEnabled}
                  />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
            <StyledGridRow>
              <StyledGridCol span={16}>
                <FormItem name="comments" label={labels?.comments}>
                  <TextField
                    inputType="textarea"
                    placeholder="Comments"
                    variant="outlined"
                  />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
            <StyledGridRow>
              <StyledGridCol>
                <FormItem
                  name="enterBusiness"
                  valuePropName="checked"
                  label="Business info?"
                  layout="horizontal"
                  className="form-item-checkbox-left"
                  style={{ marginBottom: "0" }}
                >
                  <Checkbox />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
            {enterBusiness && (
              <BusinessInformation bookingsFieldsEnabled={isEnabled} />
            )}
          </Form>
        </FormContainer>

        {isTransportModal && (
          <TransportDocumentModal
            show={isTransportModal}
            onHide={() => setIsTransportModal(false)}
          />
        )}

        {showBlockSuggestion && (
          <BlockSuggestionPopup
            showBlockSuggestion={showBlockSuggestion}
            setShowBlockSuggestion={setShowBlockSuggestion}
            data={suggestionData}
            setData={setSuggestionData}
          />
        )}
        {showCellSuggestion && (
          <CellSuggestionPopup
            showCellSuggestion={showCellSuggestion}
            setShowCellSuggestion={setShowCellSuggestion}
            data={suggestionData}
            setData={setSuggestionData}
          />
        )}
      </>
    );
  }
);

export default InmateMovementDetails;
